/**
 * Expected i18n keys. This list is the source of truth for which keys *should*
 * exist; it is not a source of values.
 *
 * Maintain this file whenever product copy starts or stops being expected:
 *   - `appState.t("some.key")` in the frontend
 *   - stable `api.*` keys used by the backend
 *   - skill command names such as `skill.<id>.command`
 *
 * How it is used:
 *   - Settings → i18n unions this list with rows from the database.
 *   - A key that is in this registry but not in the database is shown greyed
 *     out and is editable. Saving registers it (including an empty string).
 *   - A registered key with an empty string is not the same as an unregistered
 *     key. Both are valid, different states.
 *   - Deleting a registered key that is still listed here returns it to grey.
 *   - Extra database keys that are not in this list still appear (ad-hoc or
 *     skill-derived keys).
 *
 * Values live in `public.i18n` and are edited in Settings. Runtime translation
 * (`frontend/src/i18n.ts`) reads the database catalog only.
 *
 * See also: `frontend/src/app/workspace/kvp/kvpKeyRegistry.ts`.
 */

export const EXPECTED_I18N_KEYS = [
    // common
    "common.cancel",
    "common.proceed",
    "common.save",
    "common.saving",
    "common.edit",
    "common.delete",
    "common.back",
    "common.close",
    "common.create",
    "common.update",
    "common.loading",
    "common.error",
    "common.success",
    "common.notice",
    "common.filterPlaceholder",
    "common.confirmTitle",
    "common.confirmIrreversible",
    "common.confirmClearRevisions",
    "common.clearOpenRevisions",
    "common.editEditMode",
    "common.somethingWentWrong",
    "common.loadingEditor",
    "common.unexpectedError",
    "common.unexpectedErrorHelp",
    "common.copyError",
    "common.detail",
    "common.inherited",
    "common.or",
    "common.name",
    "common.email",
    "common.password",
    "common.address",
    "common.add",
    "common.deactivate",
    "common.link",
    "common.clearSelection",
    "common.enable",
    "common.disable",
    // brand
    "brand.name",
    // seo
    "seo.documentTitle",
    // language
    "language.label",
    "language.da",
    "language.en",
    // marketing
    "marketing.bootLoading",
    "marketing.bootMissingHome",
    "marketing.bootMissingContact",
    "marketing.signIn",
    "marketing.signUp",
    "marketing.seePrices",
    "marketing.navPrices",
    "marketing.contactMailto",
    "marketing.navWorkspace",
    "marketing.navSignin",
    "marketing.footer.companyName",
    "marketing.footer.cvr",
    "marketing.footer.address",
    "marketing.footer.email",
    "marketing.footer.prices",
    "marketing.footer.terms",
    "marketing.footer.privacy",
    "marketing.footer.currency",
    "marketing.footer.contact",
    "marketing.homepageCardAlt",
    "marketing.errorGetPage",
    "marketing.productDescription",
    "marketing.priceSeat",
    "marketing.priceTranscription",
    "marketing.priceVatNote",
    "marketing.legalFallback",
    // contact
    "contact.form.organisation",
    "contact.form.message",
    "contact.form.submit",
    "contact.form.sending",
    "contact.form.success",
    "contact.form.error",
    "contact.form.required",
    // auth
    "auth.signIn",
    "auth.signingIn",
    "auth.signInGoogle",
    "auth.invitationTitle",
    "auth.createAccount",
    "auth.fullName",
    "auth.creatingAccount",
    "auth.createAccountButton",
    "auth.signInSso",
    "auth.goHome",
    "auth.fillFields",
    "auth.registrationFailed",
    "auth.couldNotValidateInvite",
    "auth.invalidInvite",
    "auth.genericError",
    // nav
    "nav.createArtifact",
    "nav.createEvent",
    "nav.addClient",
    "nav.addNewClient",
    "nav.chat",
    "nav.profile",
    "nav.settings",
    "nav.home",
    "nav.summariserEval",
    "nav.billingLab",
    "nav.debugView",
    "nav.debugViewShort",
    "nav.signOut",
    "nav.signIn",
    "nav.create",
    "nav.menu",
    "nav.tags",
    "nav.artifacts",
    "nav.documents",
    "nav.events",
    "nav.lightMode",
    "nav.darkMode",
    "nav.toggleTheme",
    "nav.newArtifact",
    "nav.newEvent",
    "nav.addClientShort",
    // dashboard
    "dashboard.upcomingEvents",
    "dashboard.latestChats",
    "dashboard.goToChat",
    "dashboard.startTranscription",
    "dashboard.firstRunEvents",
    "dashboard.firstRunChats",
    "dashboard.noEvents",
    "dashboard.noChats",
    "dashboard.errorLatestChats",
    "dashboard.errorUpcomingEvents",
    "dashboard.durationMinutes",
    "dashboard.durationHours",
    "dashboard.durationHoursMinutes",
    // chat
    "chat.sending",
    "chat.awaitingAgent",
    "chat.streaming",
    "chat.awaitingSummarisation",
    "chat.awaitingDocumentSummarisation",
    "chat.conversationLocked",
    "chat.queued",
    "chat.queueMessage",
    "chat.earlier.title",
    "chat.earlier.legacy",
    "chat.earlier.viewHistory",
    "chat.history.title",
    "chat.history.loading",
    "chat.history.empty",
    "chat.history.failed",
    "chat.history.roleUser",
    "chat.history.roleAssistant",
    "chat.stop",
    "chat.runRunning",
    "chat.runFinished",
    "chat.runFailed",
    "chat.inputTooLongTitle",
    "chat.inputTooLongBody",
    "chat.pastedContent",
    "chat.attachedDocuments",
    "chat.attachFile",
    "chat.fileTooLarge",
    "chat.fileTypeNotAllowed",
    "chat.previewPastedContent",
    "chat.dismissAttachment",
    "chat.uploadingFile",
    "chat.sendQueuedForUpload",
    "chat.uploadFailed",
    "chat.uploadNeedsSingleTag",
    // objects
    "objects.pdf",
    // chat
    "chat.draftsIndicator",
    "chat.reset",
    "chat.resetConfirm",
    "chat.copySnapshot",
    "chat.exportFailed",
    "chat.copySnapshotFailed",
    "chat.systemMessages",
    "chat.messages",
    "chat.noConversation",
    "chat.failedToLoad",
    "chat.requestFailed",
    "chat.deleteSelected",
    "chat.deleteSelectedIntro",
    "chat.deleteSelectedError",
    "chat.objectType.artifact",
    "chat.objectType.tag",
    "chat.objectType.event",
    "chat.objectType.document",
    "chat.tool.draftArtifact",
    "chat.tool.artifactUpdate",
    "chat.tool.artifactQuery",
    "chat.tool.readingArtifact",
    "chat.tool.draftTag",
    "chat.tool.tagUpdate",
    "chat.tool.tagQuery",
    "chat.tool.event",
    "chat.tool.eventUpdate",
    "chat.tool.eventQuery",
    "chat.tool.eventSummaryRead",
    "chat.tool.error",
    "chat.tool.deleted",
    "chat.tool.noResults",
    "chat.tool.noEvents",
    "chat.tool.verifiedCitation",
    "chat.tool.readingInstructions",
    "chat.tool.readSessionSummary",
    "chat.tool.readingContext",
    "chat.tool.readingHistory",
    "chat.tool.readCurrentTime",
    "chat.tool.readConversationContext",
    "chat.tool.setSelectedObjects",
    "chat.tool.operationCompleted",
    "chat.tool.unknownResult",
    "chat.tool.revisionInvalid",
    "chat.tool.tagRevisionInactive",
    "chat.tool.userPreferencesUpdated",
    "chat.tool.skillCreated",
    "chat.tool.skillUpdated",
    "chat.tool.entityOperation",
    "chat.replay",
    // objects
    "objects.description",
    "objects.title",
    "objects.location",
    "objects.when",
    "objects.tag",
    "objects.content",
    "objects.variableName",
    "objects.scope",
    "objects.access",
    "objects.shareTo",
    "objects.event",
    "objects.source",
    "objects.artifacts",
    "objects.scope.me",
    "objects.scope.organisation",
    "objects.access.full",
    "objects.access.read",
    "objects.access.use",
    "objects.calendarActive",
    "objects.connectGoogleCalendar",
    "objects.voiceProfile",
    "objects.clearSpeakerProfile",
    "objects.calendarConnected",
    "objects.calendarConnectFailed",
    "objects.errorCreateArtifact",
    "objects.errorDeleteArtifact",
    "objects.errorUpdateArtifact",
    "objects.errorGetArtifact",
    "objects.errorDeleteArtifacts",
    "objects.errorGetDocument",
    "objects.errorUpdateDocument",
    "objects.errorDeleteDocument",
    "objects.errorDeleteDocuments",
    "objects.errorCreateTag",
    "objects.errorDeleteTag",
    "objects.errorUpdateTag",
    "objects.errorGetTag",
    "objects.errorDeleteTags",
    "objects.errorCreateEvent",
    "objects.errorDeleteEvent",
    "objects.errorUpdateEvent",
    "objects.errorGetEvent",
    "objects.errorDeleteEvents",
    "objects.generateSummary",
    "objects.viewTranscription",
    "objects.noTranscription",
    "objects.summaryStartedTitle",
    "objects.summaryStartedBody",
    "objects.summarySkippedTitle",
    "objects.summarySkippedBody",
    "objects.transcriptionTitle",
    "objects.errorFetchTranscription",
    "objects.errorStartSummary",
    "objects.list.tag",
    "objects.list.artifact",
    "objects.list.event",
    "objects.list.document",
    // transcription
    "transcription.waitingAudio",
    "transcription.loading",
    "transcription.empty",
    "transcription.copy",
    "transcription.start",
    "transcription.stop",
    "transcription.tagSpeaker",
    "transcription.clear",
    "transcription.unsavedTitle",
    "transcription.unsavedBody",
    "transcription.transcribe",
    "transcription.youMustRecord",
    "transcription.phraseDuration",
    "transcription.saveAndSummarise",
    "transcription.summarising",
    "transcription.saveTitle",
    "transcription.saveBody",
    "transcription.clearTitle",
    "transcription.clearBody",
    "transcription.summaryErrorTitle",
    "transcription.summaryErrorBody",
    "transcription.summarySkippedTitle",
    "transcription.summarySkippedBody",
    "transcription.clearFailed",
    "transcription.recordingAborted",
    "transcription.connectionLost",
    "transcription.recording",
    "transcription.micDenied",
    "transcription.defaultMic",
    "transcription.unknownSpeaker",
    // speaker
    "speaker.voiceProfile",
    "speaker.currentMic",
    "speaker.mustRecord",
    "speaker.noMic",
    "speaker.micDenied",
    "speaker.stopProcess",
    "speaker.record",
    "speaker.connecting",
    "speaker.recordingHint",
    "speaker.transcribing",
    "speaker.saved",
    "speaker.failedToken",
    "speaker.hasProfile",
    "speaker.removeHint",
    "speaker.clear",
    // profile
    "profile.title",
    "profile.preferences",
    "profile.addPreference",
    "profile.enterPreference",
    "profile.noPreferences",
    "profile.deletePreference",
    "profile.deletePreferenceBody",
    "profile.editPreference",
    "profile.newPreference",
    "profile.skills",
    "profile.skillsHint",
    "profile.addSkill",
    "profile.noSkills",
    "profile.newSkill",
    "profile.editSkill",
    "profile.deleteSkill",
    "profile.deleteSkillBody",
    "profile.skillOverridesSystem",
    "profile.skillKey",
    "profile.skillKeyHint",
    "profile.skillCommand",
    "profile.skillUseCase",
    "profile.skillPrompt",
    "profile.skillCreatesNote",
    "profile.skillNoteTitle",
    "profile.skillNoteContent",
    "profile.calendarSyncTitle",
    "profile.calendarSyncDescription",
    "profile.calendarSyncNotConnected",
    "profile.calendarSyncConnect",
    "profile.calendarSyncOn",
    "profile.calendarSyncOff",
    "profile.calendarSyncDisconnect",
    "profile.calendarSyncError",
    // settings
    "settings.kvp",
    "settings.models",
    "settings.models.providers",
    "settings.models.catalog",
    "settings.models.assignments",
    "settings.models.providersHint",
    "settings.models.default",
    "settings.models.configured",
    "settings.models.notConfigured",
    "settings.models.test",
    "settings.models.catalogHint",
    "settings.models.sync",
    "settings.models.syncDone",
    "settings.models.syncCounts",
    "settings.models.lastSync",
    "settings.models.neverSynced",
    "settings.models.enabledOnly",
    "settings.models.noModels",
    "settings.models.enable",
    "settings.models.disable",
    "settings.models.rename",
    "settings.models.unavailable",
    "settings.models.colContext",
    "settings.models.colPrice",
    "settings.models.colCapabilities",
    "settings.models.colEnabled",
    "settings.models.assignmentsHint",
    "settings.models.model",
    "settings.models.pickModel",
    "settings.models.inherits",
    "settings.models.unassigned",
    "settings.models.unusable",
    "settings.models.fallbacks",
    "settings.models.noFallbacks",
    "settings.models.addFallback",
    "settings.models.moveUp",
    "settings.models.skipped",
    "settings.models.skippedTitle",
    "settings.i18n",
    "settings.i18n.add",
    "settings.i18n.reload",
    "settings.i18n.empty",
    "settings.i18n.noMatch",
    "settings.i18n.selectKey",
    "settings.i18n.keyPlaceholder",
    "settings.i18n.newKey",
    "settings.i18n.missing",
    "settings.i18n.deleteBody",
    "settings.organisations",
    "settings.unauthorized",
    "settings.pickOrganisation",
    "settings.organisationTreeHint",
    "settings.org.noMatch",
    "settings.org.pill.set",
    "settings.org.pill.setTitle",
    "settings.org.pill.here",
    "settings.org.pill.hereTitle",
    "settings.org.pill.pays",
    "settings.org.pill.paysTitle",
    "settings.org.pill.demo",
    "settings.org.pill.demoTitle",
    "settings.org.newUnder",
    "settings.org.namePlaceholder",
    "settings.org.defaultPromptSet",
    "settings.org.noDefaultPromptSet",
    "settings.org.organisation",
    "settings.org.currentWorkspace",
    "settings.org.workHere",
    "settings.org.saveName",
    "settings.org.rename",
    "settings.org.switchWorkspace",
    "settings.org.promptSets",
    "settings.org.noPromptSets",
    "settings.org.defaultFor",
    "settings.org.unused",
    "settings.org.newPromptSet",
    "settings.org.newPromptSetTitle",
    "settings.org.defaultHint",
    "settings.org.danger",
    "settings.org.demoAccess",
    "settings.org.demoAccessHint",
    "settings.org.grantDemo",
    "settings.org.revokeDemo",
    "settings.org.deactivate",
    "settings.org.deactivateConfirm",
    "settings.org.deactivateBody",
    "settings.org.cannotDeactivateActive",
    "settings.org.deactivateHint",
    "settings.org.errorCreate",
    "settings.org.errorSave",
    "settings.org.errorSwitch",
    "settings.org.inheritedTitle",
    "settings.org.tab.billing",
    "settings.org.tab.prompts",
    "settings.org.tab.people",
    "settings.org.tab.usage",
    "settings.org.tab.danger",
    // usage tab
    "usage.scope",
    "usage.scopeHint",
    "usage.refresh",
    "usage.range.24h",
    "usage.range.7d",
    "usage.range.30d",
    "usage.range.90d",
    "usage.range.custom",
    "usage.from",
    "usage.to",
    "usage.invalidRange",
    "usage.search",
    "usage.noMatches",
    "usage.showAll",
    "usage.showFewer",
    "usage.total",
    "usage.failed",
    "usage.empty",
    "usage.unpricedTokens",
    "usage.user",
    "usage.system",
    "usage.group.expenses",
    "usage.group.revenue",
    "usage.group.net",
    "usage.revenueHint",
    "usage.net.value",
    "usage.net.revenue",
    "usage.net.cost",
    "usage.type.chat",
    "usage.type.memory",
    "usage.type.status",
    "usage.type.transcription_summary",
    "usage.type.document_summary",
    "usage.type.conversation_analysis",
    "usage.type.preference_reduction",
    "usage.type.skill_author",
    "usage.type.ticketing",
    "usage.type.tool_error",
    "usage.type.document_ocr",
    "usage.type.summarisation_test",
    "usage.type.chat_test",
    "settings.people.title",
    "settings.people.preferences",
    "settings.people.noPreferences",
    "settings.people.preferencePlaceholder",
    "settings.people.addPreference",
    "settings.people.deletePreference",
    "settings.people.deletePreferenceConfirm",
    "settings.people.savePreference",
    "settings.people.removeFromOrg",
    "settings.people.deactivatePerson",
    "settings.people.deactivateBody",
    "settings.people.inviteCreated",
    "settings.people.shareLink",
    "settings.people.emailOptional",
    "settings.people.generateLink",
    "settings.people.createInvite",
    "settings.people.invite",
    "settings.people.inheritedTitle",
    "settings.people.empty",
    "settings.people.errorSavePref",
    "settings.people.errorUpdate",
    "settings.people.errorDeactivate",
    "settings.people.errorInvite",
    "settings.prompt.back",
    "settings.prompt.inUse",
    "settings.prompt.set",
    "settings.prompt.assignedReadonly",
    "settings.prompt.identityLabel",
    "settings.prompt.prePromptLabel",
    "settings.prompt.summariserLabel",
    "settings.prompt.synthesiserLabel",
    "settings.prompt.documentSummariserLabel",
    "settings.prompt.documentSynthesiserLabel",
    "settings.prompt.errorOpen",
    "settings.prompt.errorCreate",
    "settings.prompt.errorUpdate",
    "settings.prompt.errorDelete",
    "settings.kvp.key",
    "settings.kvp.value",
    "settings.kvp.add",
    "settings.kvp.reload",
    "settings.kvp.empty",
    "settings.kvp.keyEmpty",
    "settings.kvp.keyPlaceholder",
    "settings.kvp.i18nLinks",
    "settings.kvp.kvpLinks",
    "settings.kvp.pill.i18n",
    "settings.kvp.pill.i18nTitle",
    "settings.kvp.pill.kvp",
    "settings.kvp.pill.kvpTitle",
    "settings.kvp.addSideEffect",
    "settings.kvp.removeSideEffect",
    "settings.kvp.sideEffect",
    "settings.kvp.sideEffect.what",
    "settings.kvp.sideEffect.noteTitle",
    "settings.kvp.sideEffect.startingText",
    "settings.kvp.sideEffect.i18nFieldTitle",
    "settings.kvp.sideEffect.promptParams",
    "settings.kvp.sideEffectRawJson",
    "settings.kvp.requiredParams",
    "settings.kvp.optionalParams",
    "settings.kvp.validation.parentKey",
    "settings.kvp.validation.missingPlaceholders",
    "settings.kvp.validation.invalidJson",
    "settings.kvp.validation.unknownSideEffect",
    "settings.kvp.validation.missingI18nKey",
    "settings.kvp.validation.reservedSkillId",
    "settings.kvp.skill.useCase",
    "settings.kvp.route.userAction.help",
    "settings.kvp.route.systemState.help",
    "settings.kvp.userAction.deleted",
    "settings.kvp.route.tool.help",
    "settings.kvp.tool.description",
    "settings.kvp.route.skill.help",
    // chat
    "chat.skill.paletteEmpty",
    "chat.skill.failed",
    // billing
    "billing.title",
    "billing.paidFromHere",
    "billing.managedHere",
    "billing.testMode",
    "billing.testModeHint",
    "billing.contact",
    "billing.invoiceRecipient",
    "billing.street",
    "billing.postalCode",
    "billing.city",
    "billing.country",
    "billing.cvr",
    "billing.contactRequired",
    "billing.invoices",
    "billing.invoice.download",
    "billing.invoice.creditNote",
    "billing.invoice.downloadError",
    "billing.status.none",
    "billing.status.active",
    "billing.status.pastDue",
    "billing.usagePlan",
    "billing.usagePlanHint",
    "billing.pastDueHint",
    "billing.ceiling",
    "billing.ceilingPlaceholder",
    "billing.ceilingHint",
    "billing.ceilingRequired",
    "billing.ceilingInvalid",
    "billing.ceilingError",
    "billing.card",
    "billing.noCard",
    "billing.addCard",
    "billing.changeCard",
    "billing.unpaid",
    "billing.noPaymentPath",
    "billing.registerHint",
    "billing.billedVia",
    "billing.coveredBy",
    "billing.coveredByHint",
    "billing.coveredByUnavailable",
    "billing.openPayer",
    "billing.cannotReachPayer",
    "billing.checkoutError",
    "billing.saveContactError",
    "billing.return.confirming",
    "billing.return.confirmingProgress",
    "billing.return.activated",
    "billing.return.processing",
    "billing.return.goSettings",
    // usage meter (speedometer) and blocked usage
    "meter.title",
    "meter.spentOf",
    "meter.left",
    "meter.daysLeft",
    "meter.pace.cruise",
    "meter.pace.brisk",
    "meter.pace.fast",
    "meter.sustainable",
    "meter.projection",
    "meter.lastMessage",
    "meter.exclVat",
    "meter.adjust",
    "meter.blocked.title",
    "meter.blocked.ceiling_reached",
    "meter.blocked.no_ceiling",
    "meter.blocked.no_payment_method",
    "meter.blocked.past_due",
    "meter.blocked.raise",
    "meter.blocked.openBilling",
    "meter.blocked.askAdmin",
    // eval
    "eval.tab.summariser",
    "eval.tab.chat",
    "eval.noTests",
    "eval.saveAndRun",
    "eval.tournamentStarted",
    "eval.tournamentBody",
    "eval.judgementPrompt",
    "eval.overall",
    "eval.elo",
    "eval.games",
    "eval.fillMissing",
    "eval.fillMissingBody",
    "eval.reload",
    "eval.fillMissingTitle",
    "eval.runMissing",
    "eval.runMissingAll",
    "eval.fillMissingAllTitle",
    "eval.fillMissingAllBody",
    "eval.starting",
    "eval.addModelSet",
    "eval.addAgent",
    "eval.searchEvents",
    "eval.promptNamePlaceholder",
    "eval.contextNamePlaceholder",
    "eval.kvpNamePlaceholder",
    "eval.kvpKeyPlaceholder",
    "eval.overrideValue",
    "eval.loadProductionValue",
    "eval.regeneratingTurns",
    "eval.regeneratingTurnsBody",
    "eval.disableHint",
    "eval.enableHint",
    // api
    "api.auth.incorrectCredentials",
    "api.auth.invalidInvite",
    "api.auth.inviteEmailMismatch",
    "api.auth.accountNotActive",
    "api.auth.inviteNotFound",
    "api.auth.inviteExpired",
    "api.auth.userNotFoundNoInvite",
    "api.auth.emailExists",
    "api.auth.couldNotCreateToken",
    "api.auth.couldNotValidateCredentials",
    "api.billing.noPaymentRegistry",
    "api.billing.insufficientOrgPermissions",
    "api.billing.checkoutFailed",
    "api.billing.noPaymentInfo",
    "api.unauthorized",
    "api.notFound",
    "api.forbidden.insufficientPermissions",
    "api.objects.noWriteTag",
    "api.objects.eventNotFound",
    // skill
    "skill.create_artifact.command",
] as const;

export type ExpectedI18nKey = (typeof EXPECTED_I18N_KEYS)[number];

export const EXPECTED_I18N_KEY_SET: ReadonlySet<string> = new Set(EXPECTED_I18N_KEYS);
